"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="space-y-6">
          <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted" />
          <div className="h-96 w-full animate-pulse rounded-xl bg-muted" />
          <div className="space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Failed to load post. Please try again later.</p>
        </Card>
      </div>
    );
  }

  const post = posts[0];
  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Post not found.</p>
        </Card>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      <Link
        to="/HomePage"
        className="mb-8 inline-flex items-center gap-2 text-sm text-primary hover:text-primary-hover transition-colors duration-[var(--duration-fast)]"
      >
        <ArrowLeft className="size-4" />
        Back to Home
      </Link>

      <header className="mb-8 space-y-6">
        <h1 className="font-display text-4xl font-bold leading-tight text-foreground md:text-5xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-10">
                {post.author.avatar?.["48"] && (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {post.author.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="font-medium text-foreground">{post.author.name}</span>
                <span className="text-xs">Author</span>
              </div>
            </div>
          )}

          {post.date && (
            <>
              <Separator orientation="vertical" className="h-8" />
              <div className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
              </div>
            </>
          )}
        </div>

        {(post.categories && post.categories.length > 0) || (post.tags && post.tags.length > 0) ? (
          <div className="flex flex-wrap gap-3">
            {post.categories && post.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="size-4 text-muted-foreground" />
                {post.categories.map((category, idx) => (
                  <span
                    key={category.id}
                    data-index={idx}
                    className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {post.tags.map((tag, idx) => (
                  <span
                    key={tag.id}
                    data-index={idx}
                    className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </header>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-xl shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      {post.excerpt && (
        <div className="mb-8 rounded-lg bg-muted/30 p-6">
          <p className="text-lg leading-relaxed text-muted-foreground">{post.excerpt}</p>
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none prose-headings:font-display prose-headings:font-semibold prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg prose-img:shadow-soft"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.author && (
        <Card className="mt-12 border-t">
          <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
            <Avatar className="size-16 shrink-0">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-semibold">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h4 className="font-display text-lg font-semibold text-foreground">
                About {post.author.name}
              </h4>
              <p className="text-sm text-muted-foreground">
                Contributing author at FIRST CLASS RESOURCES, LLC
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="mt-12 flex justify-center">
        <Link to="/HomePage">
          <Button variant="outline" size="lg" className="gap-2">
            <ArrowLeft className="size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();
  const currentPostType = wvcClient.getCurrentPostType();

  return (
    <PostsProvider postType={currentPostType} wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
      <SinglePostContent />
    </PostsProvider>
  );
}